import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Spinner } from "@shopify/polaris";

// ADD THIS GUARD WRAPPER
function WelcomeGuard({ shopOrigin, children }) {
    const navigate = useNavigate();
    const [checking, setChecking] = useState(true);

    useEffect(() => {
        if (!shopOrigin) {
            setChecking(false);
            return;
        }

        const checkStatus = async () => {
            try {
                const params = new URLSearchParams(window.location.search);
                const host = params.get("host");
                const response = await axios.get(
                    `/analytics/check-shop-data/?shop=${shopOrigin}`
                );
                const welcomeShown = response.data?.welcome_shown === true;

                if (welcomeShown) {
                    // Already seen welcome → redirect to dashboard
                    navigate(
                        `/dashboard?shop=${shopOrigin}&host=${host}`,
                        { replace: true }
                    );
                } else {
                    setChecking(false);
                }
            } catch (err) {
                // On error, just show the page
                setChecking(false);
            }
        };

        checkStatus();
    }, [shopOrigin, navigate]);

    if (checking) {
        return (
            <div style={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#F6F6F7"
            }}>
                <Spinner size="large" />
            </div>
        );
    }

    return children;
}

function WelcomePage({ shopOrigin }) {
    const navigate = useNavigate();
    const [articleCount, setArticleCount] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!shopOrigin) return;

        const fetchArticleCount = async () => {
            try {
                const params = new URLSearchParams({ shop: shopOrigin });
                const idToken = await window.shopify.idToken();

const response = await axios.get(
    `/analytics/get-articles-blogs/?${params.toString()}`,
    {
        headers: {
            Authorization: `Bearer ${idToken}`,
        },
    }
);
                if (response.data.status === "success") {
                    const count = response.data.articles?.length || 0;
                    setArticleCount(count);
                }
            } catch (err) {
                console.error("Error fetching article count:", err);
                setArticleCount(0);
            } finally {
                setLoading(false);
            }
        };

        fetchArticleCount();
    }, [shopOrigin]);

    const handleGoToDashboard = async () => {
        const params = new URLSearchParams(window.location.search);
        const host = params.get("host");
        try {
            await axios.post(`/analytics/mark-welcome-shown/?shop=${shopOrigin}`);
        } catch (err) {
            console.error("Failed to mark welcome shown:", err);
        }
        navigate(`/dashboard?shop=${shopOrigin}&host=${host}`);
    };

    const customStyles = `
    .wp-wrap {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 40px 20px;
      background: #F6F6F7;
    }
    .wp-inner {
      max-width: 520px;
      width: 100%;
    }
    .wp-title {
      font-size: 18px;
      font-weight: 600;
      color: #1A1A1A;
      margin: 0 0 8px;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .wp-sub {
      font-size: 15px;
      color: #6B7280;
      margin: 0 0 20px;
    }
    .wp-pill {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      color: #6B7280;
      margin-bottom: 20px;
    }
    .wp-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #22C55E;
      flex-shrink: 0;
      animation: pulse 1.8s ease-in-out infinite;
    }
    @keyframes pulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.5; transform: scale(0.85); }
    }
    .wp-checks {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-bottom: 16px;
    }
    .wp-check {
      font-size: 14px;
      color: #6B7280;
      margin: 0;
    }
    .wp-footer {
      font-size: 13px;
      color: #9CA3AF;
      margin-bottom: 24px;
      line-height: 1.6;
    }
    .wp-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      background: #005BD3;
      color: #fff;
      border: none;
      border-radius: 8px;
      padding: 10px 24px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: background 0.15s;
    }
    .wp-btn:hover { background: #0049B0; }
    .wp-btn:active { transform: scale(0.98); }
    .wp-info-box {
    background: #F1F6FD;
    border-radius: 8px;
    padding: 16px;
    display: flex;
    gap: 12px;
    margin-bottom: 24px;
    border: 1px solid #005BD3;
  }
  .wp-info-icon {
    background: #005BD3;
    color: #fff;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 600;
    flex-shrink: 0;
    margin-top: 2px;
  }
  .wp-info-title {
    font-size: 14px;
    font-weight: 600;
    color: #1A1A1A;
    margin: 0 0 4px;
  }
  .wp-info-text {
    font-size: 13px;
    color: #4B5563;
    margin: 0;
    line-height: 1.5;
  }
  `;

    return (
        <WelcomeGuard shopOrigin={shopOrigin}>
            <div className="wp-wrap">
                <style>{customStyles}</style>
                <div className="wp-inner">
                    <h2 className="wp-title">🎉 Welcome to AnalyticsHero</h2>
                    <p className="wp-sub">Your blog tracking is active.</p>

                    <div className="wp-pill">
                        <span className="wp-dot" />
                        {loading ? (
                            <Spinner size="small" />
                        ) : (
                            <>
                                We found{" "}
                                <strong style={{ color: "#1A1A1A", margin: "0 3px" }}>
                                    {articleCount} blog post{articleCount !== 1 ? "s" : ""}
                                </strong>{" "}
                                and started collecting insights.
                            </>
                        )}
                    </div>

                    <div className="wp-checks">
                        <p className="wp-check">✓ No setup required</p>
                        <p className="wp-check">✓ Product clicks are tracked automatically</p>
                        <p className="wp-check">✓ AI recommendations ready</p>
                    </div>

                    <div className="wp-info-box">
                        <div className="wp-info-icon">i</div>
                        <div>
                            <p className="wp-info-title">Tracking starts today</p>
                            <p className="wp-info-text">
                                Your blog is now being tracked. Shopify doesn't provide historical analytics, so your dashboard will populate with data as new visitors interact with your content.
                            </p>
                        </div>
                    </div>

                    <button className="wp-btn" onClick={handleGoToDashboard}>
                        Go to Dashboard →
                    </button>
                </div>
            </div>
        </WelcomeGuard>
    );
}

export default WelcomePage;

